// booking-dates-and-spaces.component.ts
import {NgForOf, NgIf} from "@angular/common";
import {Component, EventEmitter, Input, OnInit, Output} from "@angular/core";
import {FlexModule} from "@angular/flex-layout";
import {MatButtonModule} from "@angular/material/button";
import {MatCheckboxChange, MatCheckboxModule} from "@angular/material/checkbox";
import {MatChipsModule} from "@angular/material/chips";
import {MatDatepickerInput, MatDatepickerModule} from "@angular/material/datepicker";
import {MatFormFieldModule} from "@angular/material/form-field";
import {MatIconModule} from "@angular/material/icon";
import {MatInputModule} from "@angular/material/input";
import {MatRadioModule} from "@angular/material/radio";
import {
  SharedSpaceSelectionListComponent
} from "@features/leasing/lease-bookings/manage-sublease-booking/shared-space-selection-list/shared-space-selection-list.component";
import {LeaseSummary} from "@features/leasing/leasing.model";
import {SelectableCategory} from "@features/settings/industries/industry.model";
import {AppCoreModule} from "@modules/app.core.module";
import {PanelType} from "@shared-components/info-panel/info-panel.component";
import {DateUtilsService} from "@shared-services/dateUtils.service";
import {BookingConfig, BookingType, PatternType, Space} from "../../bookings.model";
import {LeaseBookingsService} from "../../lease-bookings.service";
import {SpaceSelectionListComponent} from "../../space-selection-list/space-selection-list.component";

@Component({
  selector: 'app-booking-dates-and-spaces',
  standalone: true,
  imports: [
    AppCoreModule,
    FlexModule,
    MatButtonModule,
    MatCheckboxModule,
    MatChipsModule,
    MatDatepickerModule,
    MatDatepickerInput,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatRadioModule,
    NgForOf,
    NgIf,
    SpaceSelectionListComponent,
    SharedSpaceSelectionListComponent
  ],
  templateUrl: './booking-dates-and-spaces.component.html',
  styleUrl: './booking-dates-and-spaces.component.scss'
})
export class BookingDatesAndSpacesComponent implements OnInit {
  @Input() bookingConfig: BookingConfig = new BookingConfig();
  @Output() bookingConfigChange = new EventEmitter<BookingConfig>();

  @Input() editType: 'new' | 'add' | 'edit' = 'new';
  @Input() lease: LeaseSummary | null = null;

  @Input() isSubLease: boolean = false;

  @Input() availableSpaces: {
    fullyAvailable: Space[],
    partiallyAvailable: Space[]
  } = {fullyAvailable: [], partiallyAvailable: []};
  @Output() updateAvailableSpaces = new EventEmitter<string[]>();

  @Input() availableSharedSpace: {
    totalSpace: number,
    availableSpace: number
  } = {totalSpace: 0, availableSpace: 0};

  @Output() previous = new EventEmitter<void>();
  @Output() next = new EventEmitter<any>();

  @Output() quickDateSelection = new EventEmitter<{
    dateRange: { start: string, end: string },
    dateSelection: string[]
  }>();

  @Input() allIndustries: SelectableCategory[] = [];

  selectedDates: string[] = [];
  selectedSpaces: string[] = [];
  reservedSpace: number = 0;

  weekDays = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

  selectDatesControl: { start: Date | null, end: Date | null } = {start: null, end: null};

  showFullPreselectedDates: boolean = false;
  showFullSelectedDates: boolean = false;

  constructor(private bookingsService: LeaseBookingsService,
              private dateUtils: DateUtilsService) {
    this.bookingConfigChange.emit(this.bookingConfig);
  }

  ngOnInit() {
    this.initEffectiveData();
  }

  getPreSelectedDatesPreview(): string {
    const preview = this.bookingConfig.preSelectedDates.sort()
      .slice(0, 3)
      .map((d) => new Date(d).toLocaleDateString())
      .join(", ")
    return this.bookingConfig.preSelectedDates.length > 3 ? `${preview} +${this.bookingConfig.preSelectedDates.length - 3} more` : preview
  }

  getPreSelectedDatesFull(): string {
    return this.bookingConfig.preSelectedDates.sort()
      .map((d) => new Date(d).toLocaleDateString())
      .join(", ")
  }

  toggleFullSelectedDates(type: 'selected' | 'preselected') {
    if (type === 'preselected') {
      this.showFullPreselectedDates = !this.showFullPreselectedDates;
    } else {
      this.showFullSelectedDates = !this.showFullSelectedDates;
    }
  }

  toggleIncludeFridays(event: MatCheckboxChange) {
    this.bookingConfig.includeDays = [];
    if (event.checked) {
      this.bookingConfig.includeDays.push("Friday");
    }
    this.updateEffectiveDates()
  }

  initEffectiveData() {
    if (this.bookingConfig.preSelectedDates.length > 0) {
      this.selectedDates = [...this.bookingConfig.preSelectedDates]
      if (this.editType != 'edit') {
        this.bookingConfig.startDate = this.bookingConfig.preSelectedDates[0];
        this.bookingConfig.endDate = this.bookingConfig.preSelectedDates[this.bookingConfig.preSelectedDates.length - 1];
      }
      if (!this.bookingConfig.patternType) {
        this.bookingConfig.patternType = this.bookingsService.getDefaultPatternType(this.bookingConfig.preSelectedDates);
      }
    }

    this.updateEffectiveDates();

    if (this.selectedDates.length > 0) {
      this.updateAvailableSpaces.emit(this.selectedDates);
    }

    if (this.bookingConfig.spaceIds.length > 0) {
      this.selectedSpaces = [...this.bookingConfig.spaceIds]
    }

    if (this.bookingConfig.subLeaseSize) {
      this.reservedSpace = this.bookingConfig.subLeaseSize;
    }
  }

  updatePatternType() {
    this.bookingConfig.includeDays = [];
    this.bookingConfig.bookingType = BookingType.SIMPLE;
    if (this.bookingConfig.patternType == PatternType.WEEKLY) {
      this.bookingConfig.bookingType = BookingType.COMPLEX;
    }
    if (this.bookingConfig.patternType == PatternType.WEEKLY_CUSTOM) {
      this.bookingConfig.includeDays = [...this.weekDays];
      this.bookingConfig.bookingType = BookingType.COMPLEX;
    }
    if (this.bookingConfig.patternType == PatternType.DAILY_CUSTOM) {
      this.selectedDates = [...this.bookingConfig.preSelectedDates];
      this.bookingConfig.bookingType = BookingType.COMPLEX;
    } else {
      this.updateEffectiveDates();
    }
  }

  updateEffectiveDates() {
    this.selectedDates = this.bookingsService.generateDatesFromPattern(
      this.bookingConfig.startDate,
      this.bookingConfig.endDate,
      this.bookingConfig.patternType,
      this.bookingConfig.includeDays,
    )

    this.bookingConfig.selectedDates = [...this.selectedDates];
    if (this.selectedDates.length > 0) {
      this.updateAvailableSpaces.emit(this.selectedDates);
    }
  }

  getEffectiveDatesPreview(): string {
    const preview = this.selectedDates.sort()
      .slice(0, 10)
      .map((d) => new Date(d).toLocaleDateString())
      .join(", ")
    return this.selectedDates.length > 10 ? `${preview} +${this.selectedDates.length - 10} more dates` : preview
  }

  getEffectiveDatesFull(): string {
    return this.selectedDates.sort()
      .map((d) => new Date(d).toLocaleDateString())
      .join(", ");
  }

  onSelectDateRange() {
    this.updateEffectiveDates();

    if (this.selectDatesControl.start && this.selectDatesControl.end) {
      const searchDates = {
        start: this.dateUtils.displayShortDate(this.selectDatesControl.start)!,
        end: this.dateUtils.displayShortDate(this.selectDatesControl.end)!
      }

      this.quickDateSelection.emit({dateRange: searchDates, dateSelection: this.selectedDates});
      this.emitBookingDatesChange();
    }
  }

  emitBookingDatesChange() {
    this.bookingConfig.selectedDates = this.selectedDates;
    this.bookingConfigChange.emit(this.bookingConfig);
  }

  toggleCustomDay(day: string) {
    const index = this.bookingConfig.includeDays.indexOf(day)
    if (index > -1) {
      this.bookingConfig.includeDays.splice(index, 1)
    } else {
      this.bookingConfig.includeDays.push(day)
    }
    this.updateEffectiveDates()
  }

  get containerHeight() {
    let height = 467;

    if (this.bookingConfig.preSelectedDates.length > 0) height = height + 110;
    if (this.bookingConfig.patternType == PatternType.WEEKENDS) height = height + 55;
    if (this.bookingConfig.patternType == PatternType.WEEKLY_CUSTOM) height = height + 200;
    if (this.showFullPreselectedDates) {
      const dateLines = Math.ceil(this.bookingConfig.preSelectedDates.length / 7);
      height = height + (dateLines - 1) * 13;
    }
    if (this.showFullSelectedDates) {
      const dateLines = Math.ceil(this.bookingConfig.preSelectedDates.length / 6);
      height = height + (dateLines - 2) * 13;
    }

    return height + 'px';
  }

  onSelectSpace(spaceId: string) {
    this.selectedSpaces = this.bookingsService.onSelectSpace(spaceId, this.selectedSpaces, this.bookingConfig);
    this.emitSpaceIdsChange();
  }

  onSharedSpaceSizeChange(spaceSize: number) {
    this.reservedSpace = spaceSize;
    this.emitSpaceIdsChange();
  }

  emitSpaceIdsChange() {
    this.bookingConfig.spaceIds = this.selectedSpaces;
    this.bookingConfig.subLeaseSize = this.reservedSpace;
    this.bookingConfigChange.emit(this.bookingConfig);
  }

  prevStep() {
    this.bookingConfigChange.emit(this.bookingConfig);
    this.previous.emit();
  }

  nextStep() {
    this.emitBookingDatesChange();
    this.next.emit();
  }

  get disableNextButton() {
    const noDatesSelected = this.selectedDates.length == 0;
    let noSpacesSelected;
    let unavailableSpaceSelected;

    if (this.isSubLease) {
      noSpacesSelected = !this.reservedSpace || this.reservedSpace <= 0;
      unavailableSpaceSelected = this.reservedSpace > this.availableSharedSpace.availableSpace;
    } else {
      noSpacesSelected = !this.bookingConfig.spaceIds || this.bookingConfig.spaceIds.length == 0;
      unavailableSpaceSelected = this.selectedSpaces.some(s => this.availableSpaces.partiallyAvailable.some(a => a.id == s));
    }

    return noSpacesSelected || noDatesSelected || unavailableSpaceSelected;
  }

  protected readonly PanelType = PanelType;
  protected readonly PatternType = PatternType;
}
